// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Modal,
  Tooltip,
} from "@mui/material";

import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { UserContext } from "context/user";
import dayjs from "dayjs";
import ModalAddPlantilla from "layouts/usuarios/ModalAddPlantilla";
import { formatMoneyPunto, makeNiceText } from "utils/formatters";
import { validateDNI, validateEmail, validateUsername } from "utils/validations";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflowY: "auto",
  p: 3,
};

export default function ModalDocente({ open, handleClose, refetch, selectedUsuario, tipo }) {
  const [usuario, setUsuario] = useState();
  const [permisosGroup, setPermisosGroup] = useState([]);
  const [expandedPermisos, setExpandedPermisos] = useState([]);
  const [selectedPermisos, setSelectedPermisos] = useState([]);
  const [newTempPassword, setNewTempPassword] = useState();
  const { handleSnackbar } = useContext(MessageManager);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAddPlantiilla, setOpenModalAddPlantiilla] = useState(false);
  const [openMenuPlantilla, setOpenMenuPlantilla] = useState(false);
  const { confirmar } = useContext(ConfirmarContext);
  const [administrarPlantillas, setAdministrarPlantillas] = useState(false);
  const { verificarAcceso } = useContext(UserContext);

  const { data: permisos } = useQuery(
    gql`
      query getPermisos($filter: JSON) {
        permisos(filter: $filter) {
          id
          nombre
          pantalla
        }
      }
    `,
    {
      variables: {
        filter: {
          id: { _lt: 100 },
        },
      },
    }
  );

  const { data: paises } = useQuery(
    gql`
      query getPaises {
        paises {
          id
          nombre
          nacionalidad
        }
      }
    `
  );

  const { data: usuarios } = useQuery(
    gql`
      query getUsuarios {
        usuarios {
          id
          username
          email
        }
      }
    `
  );

  const [saveUsuario, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveUser($input: UserInput!) {
        saveUser(input: $input) {
          id
        }
      }
    `
  );

  const [restoreTempPassword] = useMutation(
    gql`
      mutation restoreTempPassword($id: ID!) {
        restoreTempPassword(id: $id) {
          id
          tempPassword
        }
      }
    `
  );

  const [
    getPlantillas,
    { data: plantillas, refetch: refetchPlantillas, loading: loadingPlantillas },
  ] = useLazyQuery(
    gql`
      query getPlantillas($page: Int, $order: Order, $filter: JSON) {
        plantillas(pagination: { page: $page, limit: 50 }, order: $order, filter: $filter) {
          id
          nombre
          user {
            id
            nombre
            apellido
            username
          }
          plantillaDetalles {
            id
            permiso {
              id
            }
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );

  const [deletePlantilla, { loading: loadingDeletePlantilla }] = useMutation(
    gql`
      mutation deletePlantilla($id: ID!) {
        deletePlantilla(id: $id) {
          id
        }
      }
    `
  );

  const handleSave = () => {
    saveUsuario({
      variables: {
        input: {
          ...usuario,
          tipoUser: tipo,
          username: usuario?.username || usuario?.email,
          esDiscapacitado: usuario?.esDiscapacitado === "Si",
          idsPermisos: [200],
        },
      },
    })
      .then(() => {
        refetch();
        handleSnackbar("Usuario guardado correctamente", "success");
        handleClose();
      })
      .catch((e) => {
        handleSnackbar(e.message || "Error al guardar el usuario", "error");
      });
  };

  useEffect(() => {
    setAdministrarPlantillas(false);
    if (open) getPlantillas({ variables: { page: 1, filter: { tipo: "Permiso" } } });
    if (selectedUsuario) {
      setUsuario({
        id: selectedUsuario.id,
        nombre: selectedUsuario.nombre,
        apellido: selectedUsuario.apellido,
        nroLegajo: selectedUsuario.nroLegajo,
        dni: selectedUsuario.dni,
        fechaNacimiento: selectedUsuario.fechaNacimiento,
        idPais: selectedUsuario.pais?.id,
        genero: selectedUsuario.genero,
        esDiscapacitado: selectedUsuario.esDiscapacitado ? "Si" : "No",
        discapacidad: selectedUsuario.discapacidad,
        idsPermisos: selectedUsuario.idsPermisos,
      });
      if (selectedUsuario.idsPermisos) {
        setSelectedPermisos(selectedUsuario.idsPermisos);
        setNewTempPassword();
        setExpandedPermisos([]);
      } else {
        setSelectedPermisos([]);
        setNewTempPassword();
        setExpandedPermisos([]);
      }
    } else {
      setUsuario();
      setSelectedPermisos([]);
      setExpandedPermisos([]);
      setNewTempPassword();
    }
  }, [selectedUsuario, open]);

  useEffect(() => {
    if (permisos) {
      //Agrupar permisos por pantalla
      let permisosGroup = [];
      permisos.permisos.forEach((permiso) => {
        let permisoGroup = permisosGroup.find((pg) => pg.pantalla === permiso.pantalla);
        if (permisoGroup) {
          permisoGroup.permisos.push(permiso);
        } else {
          permisosGroup.push({
            pantalla: permiso.pantalla,
            permisos: [permiso],
          });
        }
      });
      setPermisosGroup(permisosGroup);
    }
  }, [permisos]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">
                  {selectedUsuario
                    ? `Editar ${tipo.toLowerCase()}`
                    : `Agregar ${tipo.toLowerCase()}`}
                </SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="username">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Email de usuario
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.email || selectedUsuario?.username}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="nroLegajo">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nº de Legajo
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.nroLegajo || "Sin legajo"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="nombre">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Apellido y nombre
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {makeNiceText(`${usuario?.apellido} ${usuario?.nombre}`, "nombre")}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel htmlFor="dni">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      DNI
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.dni
                      ? formatMoneyPunto(selectedUsuario?.dni)
                      : "Sin especificar"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={tipo === "Empleado" ? 3 : 4}>
                  <InputLabel htmlFor="fechaNacimiento">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Fecha de nacimiento
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.fechaNacimiento
                      ? dayjs(selectedUsuario?.fechaNacimiento).format("DD/MM/YYYY")
                      : "Sin especificar"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={tipo === "Empleado" ? 3 : 4}>
                  <InputLabel htmlFor="nacionalidad">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Nacionalidad
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.pais?.id
                      ? paises?.paises?.find((p) => p.id === selectedUsuario?.pais?.id)
                          ?.nacionalidad
                      : "Sin especificar"}
                  </SoftTypography>
                </Grid>
                <Grid item xs={12} sm={tipo === "Empleado" ? 3 : 4}>
                  <InputLabel htmlFor="genero">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Genero
                    </SoftTypography>
                  </InputLabel>
                  <SoftTypography variant="h6" fontWeight="bold">
                    {selectedUsuario?.genero || "Sin especificar"}
                  </SoftTypography>
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="space-between"
              alignItems="center"
            >
              <SoftBox>
                {verificarAcceso(34) &&
                  selectedUsuario &&
                  (selectedUsuario?.tempPassword || newTempPassword) &&
                  selectedUsuario?.tipoUser !== "Administrador" && (
                    <Tooltip title="Copiar contraseña temporal" placement="top">
                      <SoftButton
                        color="uproYellow"
                        circular
                        onClick={() => {
                          if (newTempPassword) {
                            navigator.clipboard.writeText(newTempPassword);
                          } else if (selectedUsuario.tempPassword) {
                            navigator.clipboard.writeText(selectedUsuario.tempPassword);
                          }
                          handleSnackbar("Contraseña temporal copiada", "success");
                        }}
                        fullWidth={{
                          xs: true,
                          sm: false,
                        }}
                      >
                        <Icon sx={{ fontWeight: "light" }}>copy</Icon>
                        &nbsp;Contraseña temporal
                      </SoftButton>
                    </Tooltip>
                  )}
              </SoftBox>
              <SoftBox
                display={{
                  xs: "flex-row",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftBox
                  mr={{
                    xs: 0,
                    sm: 2,
                  }}
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="primary"
                    circular
                    onClick={handleClose}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                  >
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="uproGreen"
                    circular
                    disabled={
                      loadingSave ||
                      (selectedUsuario
                        ? !usuario?.nombre ||
                          !usuario?.apellido ||
                          (usuario?.esDiscapacitado === "Si" && !usuario?.discapacidad) ||
                          (usuario?.nombre === selectedUsuario.nombre &&
                            usuario?.apellido === selectedUsuario.apellido &&
                            selectedPermisos === selectedUsuario.idsPermisos &&
                            usuario?.nroLegajo === selectedUsuario.nroLegajo &&
                            usuario?.dni === selectedUsuario.dni &&
                            usuario?.fechaNacimiento === selectedUsuario.fechaNacimiento &&
                            usuario?.idPais === selectedUsuario.pais?.id &&
                            usuario?.genero === selectedUsuario.genero &&
                            usuario?.esDiscapacitado ===
                              (selectedUsuario.esDiscapacitado ? "Si" : "No") &&
                            usuario?.discapacidad === selectedUsuario.discapacidad)
                        : !usuario?.email ||
                          (tipo === "Empleado" && !usuario?.username) ||
                          !usuario?.nombre ||
                          !usuario?.apellido ||
                          !usuario?.dni ||
                          (usuario?.esDiscapacitado === "Si" && !usuario?.discapacidad) ||
                          (usuario?.dni && !validateDNI(usuario?.dni)) ||
                          (usuario?.fechaNacimiento &&
                            dayjs(usuario?.fechaNacimiento).isAfter(
                              dayjs().subtract(17, "year")
                            )) ||
                          !validateEmail(usuario?.email) ||
                          (tipo === "Empleado" && !validateUsername(usuario?.username)) ||
                          usuarios?.usuarios.find((u) => u.username === usuario?.username) ||
                          usuarios?.usuarios.find((u) => u.email === usuario?.email))
                    }
                    onClick={handleSave}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                  >
                    {loadingSave ? (
                      <CircularProgress
                        size={15}
                        sx={{
                          marginRight: 0.5,
                        }}
                        color="inherit"
                      />
                    ) : (
                      <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    )}
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <ModalAddPlantilla
              open={openModalAddPlantiilla}
              handleClose={() => setOpenModalAddPlantiilla(false)}
              permisos={selectedPermisos}
              refetch={refetchPlantillas}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalDocente.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  selectedUsuario: PropTypes.object,
  tipo: PropTypes.string,
};
